import {
  DesiredActionKey,
  AnalyzeTodoClarifyingQuestion,
} from '@dreamai/types';

/**
 * Frontend-side field definitions for desired actions.
 * Mirrors the backend registry's field structure.
 */

export type ActionFieldType = 'text' | 'oauth_email_select';

export interface ActionFieldDef {
  fieldKey: string;
  fieldType: ActionFieldType;
  label: string;
}

/**
 * Short display labels derived from action keys.
 * Used in dropdowns, pills, and other compact UI elements.
 */
export const ACTION_KEY_LABELS: Record<DesiredActionKey, string> = {
  buy: 'Buy',
  reserve: 'Reserve',
  research: 'Research',
  present_options: 'Present Options',
  email_from_assistant: 'Email from secretary',
  draft_email_from_user: 'Draft Email from You',
  phone_from_assistant: 'Phone',
  fill_in_by_assistant: 'Fill In',
  send_calendar_invite: 'Calendar',
  bring: 'Bring',
  bring_and_buy: 'Buy & Bring',
  act: 'Act',
  buy_and_act: 'Buy & Act',
  project: 'Project',
  autopilot: 'Autopilot',
  enhance: 'Enhance',
  find_more: 'Find More',
  create: 'Create',
  other: 'Other',
};

/**
 * Get the display label for an action key, with optional agent name
 * for dynamic labels like "Email from {agentName}".
 */
export function getActionKeyLabel(
  key: DesiredActionKey,
  agentName?: string,
): string {
  if (key === 'email_from_assistant' && agentName) {
    return `Email from ${agentName}`;
  }
  return ACTION_KEY_LABELS[key] || key;
}

/**
 * Default button labels per action key (singular/plural).
 * Used when the agent doesn't override labels.
 */
export const DEFAULT_ACTION_LABELS: Record<
  DesiredActionKey,
  { singular: string; plural: string }
> = {
  buy: { singular: 'Buy this', plural: 'Buy these' },
  reserve: { singular: 'Reserve this', plural: 'Reserve these' },
  research: { singular: 'Research this', plural: 'Research these' },
  present_options: {
    singular: 'Present options for this',
    plural: 'Present options for these',
  },
  email_from_assistant: {
    singular: 'Email about this',
    plural: 'Email about these',
  },
  draft_email_from_user: {
    singular: 'Draft an email about this',
    plural: 'Draft an email about these',
  },
  phone_from_assistant: {
    singular: 'Make a call about this',
    plural: 'Make a call about these',
  },
  fill_in_by_assistant: {
    singular: 'Fill this in for me',
    plural: 'Fill these in for me',
  },
  send_calendar_invite: {
    singular: 'Send a calendar invite',
    plural: 'Send calendar invites',
  },
  bring: {
    singular: 'Bring this to me',
    plural: 'Bring these to me',
  },
  bring_and_buy: {
    singular: 'Buy and bring this',
    plural: 'Buy and bring these',
  },
  act: {
    singular: 'Do this in person',
    plural: 'Do these in person',
  },
  buy_and_act: {
    singular: 'Buy & Act on this',
    plural: 'Buy & Act on these',
  },
  project: {
    singular: 'Create a project for this',
    plural: 'Create a project for these',
  },
  autopilot: {
    singular: 'Set up an autopilot for this',
    plural: 'Set up autopilots for these',
  },
  enhance: { singular: 'Enhance these', plural: 'Enhance these' },
  find_more: { singular: 'Find more options', plural: 'Find more options' },
  create: { singular: 'Create this', plural: 'Create these' },
  other: { singular: 'Other', plural: 'Other' },
};

/**
 * Field definitions per action. Only actions with extra fields are listed.
 */
export const ACTION_FIELDS: Partial<
  Record<DesiredActionKey, ActionFieldDef[]>
> = {
  buy: [
    {
      fieldKey: 'deliveryAddress',
      fieldType: 'text',
      label: 'Delivery address',
    },
  ],
  draft_email_from_user: [
    {
      fieldKey: 'fromEmailAddress',
      fieldType: 'oauth_email_select',
      label: 'Send from',
    },
  ],
  send_calendar_invite: [
    {
      fieldKey: 'fromEmailAddress',
      fieldType: 'oauth_email_select',
      label: 'Send from',
    },
  ],
  bring: [
    {
      fieldKey: 'deliveryAddress',
      fieldType: 'text',
      label: 'Delivery address',
    },
  ],
  bring_and_buy: [
    {
      fieldKey: 'deliveryAddress',
      fieldType: 'text',
      label: 'Delivery address',
    },
  ],
  buy_and_act: [
    {
      fieldKey: 'deliveryAddress',
      fieldType: 'text',
      label: 'Delivery address',
    },
  ],
  other: [
    {
      fieldKey: 'customText',
      fieldType: 'text',
      label: 'What would you like done?',
    },
  ],
};

/**
 * Get the display label for an action.
 * Expects action.label to be a string ("I will...").
 */
export function getActionLabel(action: {
  key: DesiredActionKey;
  label?: string;
}): string {
  return action.label || action.key;
}

// ─── Spending helpers ─────────────────────────────────────────────────────────

/** Action keys that involve spending or deposits. Single source of truth. */
export const SPENDING_ACTION_KEYS: ReadonlySet<string> = new Set([
  'buy',
  'reserve',
  'bring_and_buy',
  'buy_and_act',
  'act',
]);

export interface QuestionSpendApproval {
  amount: number;
  currency: string;
  isDeposit: boolean;
  description: string;
}

/**
 * Derive spend/deposit approval info from an action's clarifying question answers.
 * Returns null if no spend approval is needed.
 */
export function getQuestionSpendApproval(
  action: {
    key?: string;
    label?: string;
    spendDescription?: string;
    clarifyingQuestions?: AnalyzeTodoClarifyingQuestion[];
  },
  questionAnswers: Record<string, string>,
): QuestionSpendApproval | null {
  // Spending approval is driven by max_spend / max_deposit answers, not action key.
  // Any action that includes these questions will trigger the approval modal.
  const description =
    action.spendDescription || action.label || action.key || 'this task';
  const maxSpendAnswer =
    questionAnswers['max_spend'] || questionAnswers['max_deposit'];
  if (!maxSpendAnswer) return null;
  const amount = parseFloat(maxSpendAnswer);
  // max_deposit of 0 means "don't proceed if a deposit is required" — no modal
  if (isNaN(amount) || amount <= 0) return null;
  const isDeposit = !!questionAnswers['max_deposit'];
  const fieldKey = isDeposit ? 'max_deposit' : 'max_spend';
  const matchingQ = (action.clarifyingQuestions || []).find(
    (q) => q.fieldKey === fieldKey,
  );
  const currency = matchingQ?.currency || 'GBP';
  return { amount, currency, isDeposit, description };
}

/**
 * Whether a desired action response requires a spending mandate.
 * Covers both choice-price-based and question-based spending.
 */
export function needsSpendingMandate(
  actionKey: string,
  pendingSpendItems: unknown[],
  questionAnswers?: Record<string, string>,
): boolean {
  const hasChoicePrices =
    SPENDING_ACTION_KEYS.has(actionKey) && pendingSpendItems.length > 0;
  const maxSpend = questionAnswers?.['max_spend'];
  const maxDeposit = questionAnswers?.['max_deposit'];
  const hasQuestionSpend =
    (!!maxSpend && parseFloat(maxSpend) > 0) ||
    (!!maxDeposit && parseFloat(maxDeposit) > 0);
  return hasChoicePrices || hasQuestionSpend;
}
